import { faAngleDown } from '@fortawesome/sharp-solid-svg-icons';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from 'src/utils/tailwind';
import { Icon, IconProps } from '../Icon';

const selectVariants = cva(
  'appearance-none px-3 flex w-full rounded-md border border-background-max bg-background-main ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-foreground-alpha focus-visible:outline-none focus-visible:ring-2 focus-visible:border-accent-main focus-visible:ring-accent-alpha focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'py-1.5 pr-6 text-sm',
        md: 'py-2 pr-6 text-md',
        lg: 'py-2.5 pr-6 text-lg',
      },
      isInvalid: {
        true: 'border-negative-main focus-visible:border-negative-main focus-visible:ring-red-200',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof selectVariants> {
  isRequired?: boolean;
}

const SELECT_ICON_SIZES = {
  sm: 'xs',
  md: 'md',
  lg: 'lg',
};

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, size, isInvalid, isRequired, disabled, ...props }, ref) => {
    return (
      <div className="relative">
        <select
          className={cn(selectVariants({ className, size, isInvalid }))}
          disabled={disabled}
          aria-invalid={isInvalid}
          aria-required={isRequired}
          ref={ref}
          {...props}
        />
        <Icon
          icon={faAngleDown}
          size={SELECT_ICON_SIZES[size] as IconProps['size'] | 'md'}
          className={cn(
            'pointer-events-none absolute right-2 top-1/2 translate-y-[-45%] text-foreground-subtle',
            { 'right-2.5': size === 'md' },
            { 'right-3': size === 'lg' },
            { 'opacity-50': disabled },
          )}
        />
      </div>
    );
  },
);

Select.displayName = 'Select';

export { Select };
